import React, { useState, useEffect, useCallback } from 'react';
import { X, Star, ChevronRight, ChevronLeft, Send, Gift, CheckCircle, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// NPS Scale Component
const NPSScale = ({ value, onChange }) => {
  const getColor = (score) => {
    if (score <= 6) return 'bg-red-500 hover:bg-red-600';
    if (score <= 8) return 'bg-yellow-500 hover:bg-yellow-600';
    return 'bg-green-500 hover:bg-green-600';
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between text-xs text-gray-500">
        <span>Not likely at all</span>
        <span>Extremely likely</span>
      </div>
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
          <button
            key={score}
            type="button"
            onClick={() => onChange(score)}
            className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
              value === score 
                ? `${getColor(score)} text-white scale-110 shadow-lg` 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {score}
          </button>
        ))}
      </div>
    </div>
  );
};

// Star Rating Component
const StarRating = ({ value, onChange, max = 5 }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex gap-1">
      {[...Array(max)].map((_, i) => {
        const starValue = i + 1;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onChange(starValue)}
            onMouseEnter={() => setHovered(starValue)}
            onMouseLeave={() => setHovered(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`h-8 w-8 ${
                starValue <= (hovered || value)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'fill-gray-200 text-gray-300'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};

// Question Renderer
const QuestionRenderer = ({ question, value, onChange }) => {
  switch (question.type) {
    case 'short_text':
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Your answer..."
          className="mt-2"
          data-testid={`question-${question.id}`}
        />
      );

    case 'long_text':
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Your detailed feedback..."
          rows={4}
          className="mt-2"
          data-testid={`question-${question.id}`}
        />
      );

    case 'rating':
      return (
        <div className="mt-3">
          <StarRating value={value || 0} onChange={onChange} />
        </div>
      );

    case 'nps':
      return (
        <div className="mt-3">
          <NPSScale value={value} onChange={onChange} />
        </div>
      );

    case 'yes_no':
      return (
        <RadioGroup value={value?.toString()} onValueChange={(v) => onChange(v === 'true')} className="mt-3">
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="true" id={`${question.id}-yes`} />
              <Label htmlFor={`${question.id}-yes`}>Yes</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="false" id={`${question.id}-no`} />
              <Label htmlFor={`${question.id}-no`}>No</Label>
            </div>
          </div>
        </RadioGroup>
      );

    case 'multiple_choice':
      return (
        <RadioGroup value={value} onValueChange={onChange} className="mt-3 space-y-2">
          {(question.options || []).map((option, i) => (
            <div key={i} className="flex items-center gap-2">
              <RadioGroupItem value={option} id={`${question.id}-${i}`} />
              <Label htmlFor={`${question.id}-${i}`}>{option}</Label>
            </div>
          ))}
        </RadioGroup>
      );

    case 'multi_select':
      return (
        <div className="mt-3 space-y-2">
          {(question.options || []).map((option, i) => (
            <div key={i} className="flex items-center gap-2">
              <Checkbox
                id={`${question.id}-${i}`}
                checked={(value || []).includes(option)}
                onCheckedChange={(checked) => {
                  const current = value || [];
                  if (checked) {
                    onChange([...current, option]);
                  } else {
                    onChange(current.filter((v) => v !== option));
                  }
                }}
              />
              <Label htmlFor={`${question.id}-${i}`}>{option}</Label>
            </div>
          ))}
        </div>
      );

    case 'dropdown':
      return (
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full border rounded-lg p-2"
          data-testid={`question-${question.id}`}
        >
          <option value="">Select an option...</option>
          {(question.options || []).map((option, i) => (
            <option key={i} value={option}>{option}</option>
          ))}
        </select>
      );

    default:
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Your answer..."
          className="mt-2"
        />
      );
  }
};

// Main Feedback Widget Component
export default function FeedbackWidget({ 
  surveyId,
  onComplete,
  onClose,
  position = 'bottom-right', // bottom-right, bottom-left, center
  trigger = 'button' // button, auto, slide-in
}) {
  const [isOpen, setIsOpen] = useState(trigger === 'auto');
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [completed, setCompleted] = useState(false);
  const [incentiveInfo, setIncentiveInfo] = useState(null);

  // Fetch survey details
  const fetchSurvey = useCallback(async () => {
    if (!surveyId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback/templates/${surveyId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSurvey(data);
      }
    } catch (error) {
      console.error('Failed to load survey:', error);
    }
    setLoading(false);
  }, [surveyId]);

  useEffect(() => {
    if (isOpen) {
      fetchSurvey();
    }
  }, [isOpen, fetchSurvey]);

  // Handle answer change
  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  // Validate current step
  const isStepValid = () => {
    if (!survey?.questions) return false;
    const question = survey.questions[currentStep];
    if (!question) return false;
    
    if (question.required) {
      const answer = answers[question.id];
      if (answer === undefined || answer === null || answer === '') return false;
      if (Array.isArray(answer) && answer.length === 0) return false;
    }
    return true;
  };

  // Submit survey
  const handleSubmit = async () => {
    if (!survey) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback/user/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          survey_id: surveyId,
          answers,
          success_story_consent: false
        })
      });

      if (res.ok) {
        const data = await res.json();
        setCompleted(true);
        setIncentiveInfo(data.incentive_awarded || data.cash_incentive);
        if (onComplete) onComplete(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit feedback');
      }
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
    setSubmitting(false);
  };

  // Close widget
  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Position styles
  const positionStyles = {
    'bottom-right': 'fixed bottom-4 right-4',
    'bottom-left': 'fixed bottom-4 left-4',
    'center': 'fixed inset-0 flex items-center justify-center bg-black/50 z-50'
  };

  // Trigger button
  if (!isOpen && trigger === 'button') {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 z-40"
        data-testid="feedback-widget-trigger"
      >
        <MessageSquare className="h-6 w-6" />
      </button>
    );
  }

  if (!isOpen) return null;

  // Loading state
  if (loading) {
    return (
      <div className={`${positionStyles[position]} z-50`}>
        <div className="bg-white rounded-2xl shadow-2xl p-6 w-96 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  // No survey found
  if (!survey) {
    return null;
  }

  const questions = survey.questions || [];
  const totalSteps = questions.length;
  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Completed state
  if (completed) {
    return (
      <div className={`${positionStyles[position]} z-50`}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-96 max-w-[95vw] text-center" data-testid="feedback-complete">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
          <p className="text-gray-600 mb-4">Your feedback helps us improve.</p>
          
          {incentiveInfo && (
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4 mb-4">
              <Gift className="h-5 w-5 text-violet-500 mx-auto mb-2" />
              <p className="text-sm text-violet-700 font-medium">
                {incentiveInfo.type === 'billing_credit' && `You earned $${incentiveInfo.amount} billing credit!`}
                {incentiveInfo.type === 'points' && `You earned ${incentiveInfo.amount} points!`}
                {incentiveInfo.amount && !incentiveInfo.type && `You may be eligible for $${incentiveInfo.amount} reward!`}
              </p>
              {incentiveInfo.needs_payment_setup && (
                <p className="text-xs text-violet-600 mt-1">
                  Set up your payment method in settings to receive cash rewards.
                </p>
              )}
            </div>
          )}
          
          <Button onClick={handleClose} className="w-full" data-testid="close-complete">
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${positionStyles[position]} z-50`}>
      <div 
        className="bg-white rounded-2xl shadow-2xl overflow-hidden w-[420px] max-w-[95vw]"
        data-testid="feedback-widget"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">{survey.name}</h3>
              <p className="text-sm text-violet-100 opacity-90">
                Question {currentStep + 1} of {totalSteps}
              </p>
            </div>
            <button 
              onClick={handleClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              data-testid="close-widget"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Incentive Banner */}
        {(survey.incentive_type || survey.cash_incentive_enabled) && currentStep === 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
            <Gift className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-800">
              {survey.cash_incentive_enabled && survey.cash_incentive_amount
                ? `Complete for a chance to win $${survey.cash_incentive_amount}!`
                : survey.incentive_type === 'billing_credit'
                ? `Earn $${survey.incentive_amount} billing credit!`
                : `Earn ${survey.incentive_amount} points!`}
            </span>
          </div>
        )}

        {/* Question Content */}
        <div className="p-5">
          {currentQuestion && (
            <div className="min-h-[180px]">
              <Label className="text-base font-medium text-gray-900">
                {currentQuestion.label}
                {currentQuestion.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <QuestionRenderer
                question={currentQuestion}
                value={answers[currentQuestion.id]}
                onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
              />
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="border-t bg-gray-50 px-5 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            data-testid="prev-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {currentStep === totalSteps - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !isStepValid()}
              className="bg-gradient-to-r from-violet-500 to-purple-600"
              data-testid="submit-btn"
            >
              {submitting ? (
                <>Submitting...</>
              ) : (
                <>
                  Submit
                  <Send className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentStep(Math.min(totalSteps - 1, currentStep + 1))}
              disabled={!isStepValid()}
              data-testid="next-btn"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Export a hook for checking pending surveys
export function usePendingSurveys() {
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPendingSurveys = async () => {
      try {
        const res = await fetch(`${API_URL}/api/feedback/user/pending-surveys`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setPendingSurveys(data.surveys || []);
        }
      } catch (error) {
        console.error('Failed to fetch pending surveys:', error);
      }
      setLoading(false);
    };

    fetchPendingSurveys();
  }, []);

  return { pendingSurveys, loading };
}

// Export a trigger button component
export function FeedbackTriggerButton({ surveyId, children, className }) {
  const [showWidget, setShowWidget] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowWidget(true)}
        className={className}
        data-testid="feedback-trigger-custom"
      >
        {children || (
          <>
            <MessageSquare className="h-4 w-4 mr-2" />
            Give Feedback
          </>
        )}
      </button>
      {showWidget && (
        <FeedbackWidget
          surveyId={surveyId}
          position="center"
          trigger="auto"
          onClose={() => setShowWidget(false)}
        />
      )}
    </>
  );
}
